import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useUpdateJobStatus, useUpdateJobTechnician, useDeleteJob, useUpdateJobCloseDate, useUpdateJobScheduledDate, useUpdateJobServiceType, useUpdateJobNotes, useAppendJobNote, useUpdateJobRecallFlag } from '@/hooks/useJobs';
import { Checkbox } from '@/components/ui/checkbox';
import { useTechsByMetro, useServiceTypes } from '@/hooks/useReferenceData';
import { useUserRole } from '@/hooks/useUserRole';
import { useVisibility } from '@/hooks/useVisibility';
import { useJobParts } from '@/hooks/useJobParts';
import { useJobPayments } from '@/hooks/useJobPayments';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import { Wrench, FileText, Package, CreditCard, CheckCircle, XCircle, AlertTriangle, ExternalLink, Trash2, Pencil, CalendarIcon, Receipt, FolderKanban, Plus, Send, Loader2 } from 'lucide-react';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useJobProjects } from '@/hooks/useProjects';
import { usePipelines } from '@/hooks/usePipelines';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';
import { LinkJobToProjectDialog } from '@/components/projects/LinkJobToProjectDialog';
import { AddJobPartDialog } from '@/components/jobs/AddJobPartDialog';
import { AddPaymentDialog } from '@/components/jobs/AddPaymentDialog';
import { JobPartsTable } from '@/components/jobs/JobPartsTable';
import { JobPaymentsTable } from '@/components/jobs/JobPaymentsTable';
import { CopyJobDetailsButton } from '@/components/jobs/CopyJobDetailsButton';
import { useJobBrandName } from '@/hooks/useJobBrandName';
import { JobFinancialSummary } from '@/components/jobs/JobFinancialSummary';
import { FinancialsForm } from '@/components/jobs/FinancialsForm';
import { AddPartFromCatalog } from '@/components/jobs/AddPartFromCatalog';
import { CompleteJobDialog } from '@/components/jobs/CompleteJobDialog';
import { CancelJobDialog } from '@/components/jobs/CancelJobDialog';
import { JobOverrideCommissionCard } from '@/components/jobs/JobOverrideCommissionCard';
import { JobActivityLog } from '@/components/jobs/JobActivityLog';
import { TechProgressCard } from '@/components/jobs/TechProgressCard';
import { JobEstimatesCard } from '@/components/jobs/JobEstimatesCard';
import { JobPhotosCard } from '@/components/jobs/JobPhotosCard';
import { InvoiceFromJobDialog } from '@/components/invoices/InvoiceFromJobDialog';
import { EstimateFromLeadDialog } from '@/components/estimates/EstimateFromLeadDialog';
import { useJobInvoice, useDeleteInvoice } from '@/hooks/useInvoices';
import { useLinkedCustomer } from '@/hooks/useCustomers';
import { CustomerLink } from '@/components/customers/CustomerLink';
import { cn } from '@/lib/utils';
import { JobWithRelations } from '@/types';
import { CampaignAssignment } from '@/components/leads/CampaignAssignment';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { formatDocDate } from '@/utils/locale';
import { formatInTz, pickedDayKey, dayKeyToPickerDate } from '@/utils/timezone';

const statusColors = {
  scheduled: 'bg-info',
  in_progress: 'bg-warning',
  completed: 'bg-success',
  on_hold: 'bg-warning',
  quote_sent: 'bg-primary',
  cancelled: 'bg-destructive',
};

const paymentStatusColors = {
  unpaid: 'bg-destructive',
  partially_paid: 'bg-warning',
  paid: 'bg-success',
};

interface JobDetailContentProps {
  job: JobWithRelations;
  /** Whether this is rendered inside a sheet (compact mode) or as full page */
  isSheet?: boolean;
  /** Callback when job is deleted (for navigation in page context) */
  onDeleted?: () => void;
}

export function JobDetailContent({ job, isSheet = false, onDeleted }: JobDetailContentProps) {
  const navigate = useNavigate();
  // Jobs from before the contact-sync triggers have no customer_id; fall back
  // to the customer owning this phone number so the name still links.
  const { data: linkedCustomer } = useLinkedCustomer(job);
  const linkedCustomerId = job.customer_id ?? linkedCustomer?.id ?? null;
  const { data: parts = [] } = useJobParts(job.id);
  const { data: payments = [] } = useJobPayments(job.id);
  const { data: techs = [] } = useTechsByMetro(job.metro_id);
  const { data: serviceTypes = [] } = useServiceTypes();
  const updateStatus = useUpdateJobStatus();
  const updateTechnician = useUpdateJobTechnician();
  const updateCloseDate = useUpdateJobCloseDate();
  const updateScheduledDate = useUpdateJobScheduledDate();
  const updateServiceType = useUpdateJobServiceType();
  const updateNotes = useUpdateJobNotes();
  const appendNote = useAppendJobNote();
  const updateRecallFlag = useUpdateJobRecallFlag();
  const deleteJob = useDeleteJob();
  const { data: existingInvoice } = useJobInvoice(job.id);
  const { isAdmin, isCSR } = useUserRole();
  const { canSeeTechPay } = useVisibility();
  const { locale, timezone, formatMoney } = useCompanyLocale();
  const weekdayDate = `EEE, ${locale.shortDate}`;
  const todayKey = formatInTz(new Date(), timezone, 'yyyy-MM-dd');
  const scheduledKey = job.scheduled_date ? formatDocDate(job.scheduled_date, 'yyyy-MM-dd', timezone) : null;
  const [showAddPart, setShowAddPart] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCompleteJobDialog, setShowCompleteJobDialog] = useState(false);
  const [showCancelJobDialog, setShowCancelJobDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);
  const [closeDatePickerOpen, setCloseDatePickerOpen] = useState(false);
  const [pendingCloseDate, setPendingCloseDate] = useState<Date | undefined>(undefined);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(undefined);
  const [rescheduleTimeStart, setRescheduleTimeStart] = useState('');
  const [rescheduleTimeEnd, setRescheduleTimeEnd] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  /** Snapshot of job.notes when the full editor opened; the save refuses if the row moved on. */
  const [notesBase, setNotesBase] = useState<string | null>(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');

  // Projects feature flag + data
  const { enableExtendedProjects } = useCompanySettings();
  const { data: jobProjects } = useJobProjects(enableExtendedProjects ? job.id : null);
  const { data: pipelines } = usePipelines();
  const firstPipeline = pipelines?.[0] ?? null;
  const [addToProjectOpen, setAddToProjectOpen] = useState(false);
  const [createProjectForJobOpen, setCreateProjectForJobOpen] = useState(false);

  // Fetch lead data for campaign/source display
  const { data: leadData } = useQuery({
    queryKey: ['job-lead-info', job.lead_id],
    queryFn: async () => {
      if (!job.lead_id) return null;

      const { data, error } = await supabase
        .from('leads')
        .select(`
          campaign_id,
          lead_source_id,
          form_name,
          campaigns (name, external_campaign_id),
          lead_sources (name)
        `)
        .eq('id', job.lead_id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!job.lead_id,
  });

  // Same resolution the technician app gets from jobs_tech: the lead brand,
  // else the company default.
  const brandName = useJobBrandName(job.lead_id);

  // A reschedule always sends the job back to the technician when they have declined or not
  // confirmed yet. The DB trigger already does that when the date or start time actually
  // changes; for a same-day tweak (or no change at all) the office_assign_tech re-send
  // covers it, so "reschedule to the same tech" asks them again either way.
  const rescheduleReasks = !!job.assigned_tech_id && !job.arrived_at && job.tech_ack_status !== 'accepted';
  const handleReschedule = () => {
    if (!rescheduleDate) return;
    updateScheduledDate.mutate({
      jobId: job.id,
      scheduledDate: rescheduleDate,
      scheduledTimeStart: rescheduleTimeStart || null,
      scheduledTimeEnd: rescheduleTimeEnd || null,
    }, {
      onSuccess: () => {
        setShowRescheduleDialog(false);
        if (rescheduleReasks) {
          updateTechnician.mutate({
            jobId: job.id,
            technicianId: job.assigned_tech_id,
            resend: true,
            techName: (job as { techs?: { name: string } | null }).techs?.name ?? null,
          });
        }
      },
    });
  };

  const handleStatusChange = (newStatus: string) => {
    updateStatus.mutate({ jobId: job.id, status: newStatus });
  };

  // A declined job shows the field as needing a choice: picking the same technician (or
  // anyone else) sends it out again. Otherwise selecting the current name is a no-op.
  const techNeedsChoice = !!job.assigned_tech_id && job.tech_ack_status === 'declined';
  const handleTechnicianChange = (technicianId: string) => {
    const next = technicianId === 'unassigned' ? null : technicianId;
    const chosen = next ? techs.find((t) => t.id === next)?.name ?? (job as { techs?: { name: string } | null }).techs?.name ?? null : null;
    updateTechnician.mutate({
      jobId: job.id,
      technicianId: next,
      resend: !!next && next === job.assigned_tech_id,
      techName: chosen,
    });
  };

  const handleServiceTypeChange = (serviceTypeId: string) => {
    updateServiceType.mutate({
      jobId: job.id,
      serviceTypeId: serviceTypeId === 'none' ? null : serviceTypeId
    });
  };

  const handleJobComplete = () => {
    setShowCompleteJobDialog(false);
  };

  const handleJobCancel = () => {
    setShowCancelJobDialog(false);
  };

  const deleteInvoice = useDeleteInvoice();

  const handleDeleteJob = async () => {
    try {
      if (existingInvoice) {
        await deleteInvoice.mutateAsync(existingInvoice.id);
      }

      deleteJob.mutate(job.id, {
        onSuccess: () => {
          onDeleted?.();
        },
      });
    } catch (error) {
      console.error('Error deleting linked records:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Title Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className={cn("font-bold", isSheet ? "text-2xl" : "text-3xl")}>{job.job_number}</h1>
            <Badge className={statusColors[job.status as keyof typeof statusColors]} variant="default">
              {job.status.replace('_', ' ').toUpperCase()}
            </Badge>
            {job.status !== 'cancelled' && (
              <Badge className={paymentStatusColors[job.payment_status as keyof typeof paymentStatusColors]} variant="default">
                {job.payment_status.replace('_', ' ').toUpperCase()}
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Generate Invoice Button */}
            {job.status === 'completed' && !existingInvoice && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowInvoiceDialog(true)}
                className="gap-2"
              >
                <Receipt className="h-4 w-4" />
                Generate Invoice
              </Button>
            )}
            {existingInvoice && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/invoices/${existingInvoice.id}`)}
                className="gap-2"
              >
                <Receipt className="h-4 w-4" />
                View Invoice ({existingInvoice.invoice_number})
              </Button>
            )}

            <CopyJobDetailsButton job={job} />

            {isAdmin && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Job</AlertDialogTitle>
                    <AlertDialogDescription>
                      {existingInvoice ? (
                        <div className="space-y-2">
                          <p className="font-medium text-destructive">
                            ⚠️ This job has a linked invoice ({existingInvoice.invoice_number}).
                          </p>
                          <p>
                            Deleting this job will <strong>also delete the invoice</strong>.
                            This action cannot be undone.
                          </p>
                        </div>
                      ) : (
                        "This will permanently delete this job. Any linked lead will be preserved but unlinked from this job. This action cannot be undone."
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteJob}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {existingInvoice ? 'Delete Job & Invoice' : 'Delete Job'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4 text-muted-foreground flex-wrap">
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            <CustomerLink
              customerId={linkedCustomerId}
              name={job.customer_name}
              className="font-medium text-foreground"
              showUnlinkedWarning
            />
          </div>
          <div className="flex items-center gap-2">
            <span>{job.customer_phone}</span>
          </div>
          {job.customer_email && (
            <div className="flex items-center gap-2">
              <span>{job.customer_email}</span>
            </div>
          )}
        </div>
      </div>

      {/* Status Selector and Action Buttons */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Job Status:</span>
              <Select
                value={job.status}
                onValueChange={handleStatusChange}
                disabled={!isAdmin && (job.status === 'completed' || job.status === 'cancelled')}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">Ongoing</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="quote_sent">Quote Sent</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {job.status !== 'completed' && job.status !== 'cancelled' && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={() => {
                    setRescheduleDate(scheduledKey ? dayKeyToPickerDate(scheduledKey) : undefined);
                    setRescheduleTimeStart(job.scheduled_time_start || '');
                    setRescheduleTimeEnd(job.scheduled_time_end || '');
                    setShowRescheduleDialog(true);
                  }}
                  className="gap-2"
                  variant="outline"
                  size={isSheet ? "sm" : "default"}
                >
                  <CalendarIcon className="h-4 w-4" />
                  Reschedule
                </Button>
                {job.lead_id && job.status !== 'quote_sent' && (
                  <Button
                    onClick={() => setShowEstimateDialog(true)}
                    className="gap-2"
                    variant="outline"
                    size={isSheet ? "sm" : "default"}
                  >
                    <Send className="h-4 w-4" />
                    Send Estimate
                  </Button>
                )}
                <Button
                  onClick={() => setShowCompleteJobDialog(true)}
                  className="gap-2"
                  variant="default"
                  size={isSheet ? "sm" : "default"}
                >
                  <CheckCircle className="h-4 w-4" />
                  Close Job
                </Button>
                <Button
                  onClick={() => setShowCancelJobDialog(true)}
                  className="gap-2"
                  variant="destructive"
                  size={isSheet ? "sm" : "default"}
                >
                  <XCircle className="h-4 w-4" />
                  Cancel Job
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Campaign Assignment (only if lead exists and has no campaign) */}
      {job.lead_id && (
        <CampaignAssignment
          leadId={job.lead_id}
          currentCampaignId={null}
        />
      )}


      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="financials">Finance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Technician progress from the Steddy app (accept / en route / on site / finished) */}
          <TechProgressCard job={job} />

          {/* Job Information Card */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileText className="h-4 w-4 text-primary" />
                </div>
                <CardTitle className="text-lg">Job Information</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Customer Name</p>
                  <CustomerLink
                    customerId={linkedCustomerId}
                    name={job.customer_name}
                    className="font-medium"
                    showUnlinkedWarning
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Phone</p>
                  <p className="font-medium">{job.customer_phone}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Email</p>
                  <p className="font-medium">{job.customer_email || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Service Type</p>
                  <div className="flex items-center gap-2">
                    <Select
                      value={job.service_type_id || 'none'}
                      onValueChange={handleServiceTypeChange}
                    >
                      <SelectTrigger className={cn(
                        "w-full",
                        !job.service_type_id && "border-warning text-warning"
                      )}>
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not set</SelectItem>
                        {serviceTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!job.service_type_id && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Missing service type affects reporting accuracy</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Assigned Tech</p>
                  <Select
                    value={techNeedsChoice ? '' : (job.assigned_tech_id || 'unassigned')}
                    onValueChange={handleTechnicianChange}
                    disabled={updateTechnician.isPending}
                  >
                    <SelectTrigger className={cn('w-full', techNeedsChoice && 'border-destructive text-destructive')}>
                      <SelectValue placeholder={techNeedsChoice
                        ? `${(job as { techs?: { name: string } | null }).techs?.name ?? 'Technician'} declined – choose again`
                        : 'Select technician'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {techs.map((tech) => (
                        <SelectItem key={tech.id} value={tech.id}>
                          {tech.name}
                        </SelectItem>
                      ))}
                      {/* Show current tech if they are outside this metro */}
                      {job.assigned_tech_id && !techs.find(t => t.id === job.assigned_tech_id) && (
                        <SelectItem value={job.assigned_tech_id} className="text-warning">
                          {(job as any).techs?.name ?? 'Current tech'} (outside metro)
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-2">Recall</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={!!job.is_recall}
                      disabled={updateRecallFlag.isPending}
                      onCheckedChange={(checked) =>
                        updateRecallFlag.mutate({ jobId: job.id, isRecall: checked === true })
                      }
                    />
                    <span className="text-sm font-medium">Warranty / return visit</span>
                  </label>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Assigned CSR</p>
                  <p className="font-medium">{job.csr_profile?.full_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Quoted Price</p>
                  <p className="font-medium">{job.quoted_price ? formatMoney(job.quoted_price) : '-'}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Trading as</p>
                  <p className="font-medium">
                    {brandName || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Web form</p>
                  <p className="font-medium">
                    {leadData?.form_name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Campaign</p>
                  <p className="font-medium">
                    {leadData?.campaigns?.name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Lead Source</p>
                  <p className="font-medium">
                    {leadData?.lead_sources?.name || '-'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm font-medium text-muted-foreground mb-2">Scheduled</p>
                  {job.scheduled_date ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {formatDocDate(job.scheduled_date, weekdayDate, timezone)}
                        </p>
                        {scheduledKey === todayKey && (
                          <Badge variant="warning">
                            Today
                          </Badge>
                        )}
                        {scheduledKey !== null && scheduledKey < todayKey &&
                          job.status !== 'completed' && job.status !== 'cancelled' && (
                            <Badge variant="destructive-soft">
                              Overdue
                            </Badge>
                          )}
                      </div>
                      {(job.scheduled_time_start || job.scheduled_time_end || job.scheduled_time) && (
                        <p className="text-sm text-muted-foreground">
                          {job.scheduled_time_start && job.scheduled_time_end ? (
                            <>Time window: {job.scheduled_time_start} - {job.scheduled_time_end}</>
                          ) : job.scheduled_time ? (
                            <>Time: {job.scheduled_time}</>
                          ) : null}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="font-medium">-</p>
                  )}
                </div>
                {(job.status === 'completed' || job.status === 'cancelled') && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Close Date</p>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {job.job_date
                          ? formatDocDate(job.job_date, weekdayDate, timezone)
                          : job.closed_at || job.cancelled_at
                            ? formatDocDate(job.closed_at || job.cancelled_at!, weekdayDate, timezone)
                            : '-'
                        }
                      </p>
                      {(isAdmin || isCSR) && (
                        <Popover
                          open={closeDatePickerOpen}
                          onOpenChange={(open) => {
                            setCloseDatePickerOpen(open);
                            if (open) {
                              setPendingCloseDate(job.job_date ? dayKeyToPickerDate(job.job_date) : undefined);
                            }
                          }}
                        >
                          <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-6 w-6">
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={pendingCloseDate}
                              onSelect={(date) => setPendingCloseDate(date)}
                              disabled={(date) => pickedDayKey(date) > todayKey}
                              initialFocus
                              className={cn("p-3 pointer-events-auto")}
                            />
                            <div className="flex justify-end gap-2 p-3 pt-0 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCloseDatePickerOpen(false)}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                disabled={!pendingCloseDate || updateCloseDate.isPending}
                                onClick={() => {
                                  if (pendingCloseDate) {
                                    updateCloseDate.mutate({ jobId: job.id, closeDate: pendingCloseDate });
                                    setCloseDatePickerOpen(false);
                                  }
                                }}
                              >
                                {updateCloseDate.isPending ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Address</p>
                <p className="font-medium">{job.customer_address}</p>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Issue Description</p>
                <p className="whitespace-pre-wrap">{job.issue_description}</p>
              </div>

              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-muted-foreground">Notes</p>
                  {!isEditingNotes && !isAddingNote && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs gap-1"
                        onClick={() => {
                          setNewNote('');
                          setIsAddingNote(true);
                        }}
                      >
                        <Plus className="h-3 w-3" />
                        Add note
                      </Button>
                      {job.notes && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs gap-1"
                          onClick={() => {
                            setNotesValue(job.notes || '');
                            setNotesBase(job.notes || '');
                            setIsEditingNotes(true);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                          Edit all
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                {isAddingNote ? (
                  // Appended server-side with a timestamp + author line, like the technician app,
                  // so two people adding notes at once never overwrite each other.
                  <div className="space-y-2">
                    {job.notes && (
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground max-h-40 overflow-y-auto rounded-md border bg-muted/30 p-2">
                        {job.notes}
                      </p>
                    )}
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      rows={3}
                      autoFocus
                      placeholder="New note (added below the existing notes with the time and your name)…"
                      className="resize-none text-sm"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          appendNote.mutate(
                            { jobId: job.id, text: newNote },
                            { onSuccess: () => { setIsAddingNote(false); setNewNote(''); } }
                          );
                        }}
                        disabled={appendNote.isPending || !newNote.trim()}
                      >
                        {appendNote.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                        Add note
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsAddingNote(false)}
                        disabled={appendNote.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : isEditingNotes ? (
                  <div className="space-y-2">
                    <Textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      rows={6}
                      placeholder="Add notes about this job (e.g. tech updates, hold reasons, time changes)..."
                      className="resize-none text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Rewrites the whole notes field. If a technician adds a note from the app while you edit, the save is
                      refused and the latest notes are reloaded.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => {
                          updateNotes.mutate(
                            { jobId: job.id, notes: notesValue, expectedNotes: notesBase },
                            {
                              onSuccess: () => setIsEditingNotes(false),
                              onError: (err, _vars, _ctx) => {
                                const conflict = (err as Error & { conflictNotes?: string | null }).conflictNotes;
                                if (conflict !== undefined) {
                                  setNotesValue(conflict ?? '');
                                  setNotesBase(conflict ?? '');
                                }
                              },
                            }
                          );
                        }}
                        disabled={updateNotes.isPending}
                      >
                        {updateNotes.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsEditingNotes(false)}
                        disabled={updateNotes.isPending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap text-sm">
                    {job.notes ?? <span className="text-muted-foreground italic">No notes yet.</span>}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Quotes on this job's lead (office or technician app) */}
          <JobEstimatesCard job={job} />

          {/* Photos the technician took in the app */}
          <JobPhotosCard jobId={job.id} />

          {/* Projects card — only visible when feature is enabled */}
          {enableExtendedProjects && (
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FolderKanban className="h-4 w-4 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Projects</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {jobProjects && jobProjects.length > 0 ? (
                  <>
                    {jobProjects.map(project => (
                      <Link
                        key={project.id}
                        to={`/projects/${project.id}`}
                        className="flex items-center justify-between rounded-lg border px-3 py-2.5 hover:bg-muted/40 transition-colors"
                      >
                        <span className="text-sm font-medium">{project.name}</span>
                        <div className="flex items-center gap-2">
                          {project.current_status && (
                            <Badge
                              variant="outline"
                              className="text-xs gap-1.5"
                            >
                              <div className="h-2 w-2 rounded-full" style={{ backgroundColor: project.current_status.color }} />
                              {project.current_status.name}
                            </Badge>
                          )}
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setAddToProjectOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add to Another Project
                    </Button>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <p className="text-sm text-muted-foreground">This job is not part of any project.</p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCreateProjectForJobOpen(true)}
                        disabled={!firstPipeline}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Project
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAddToProjectOpen(true)}
                      >
                        Add to Existing Project
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="financials" className="space-y-6 mt-6">
          <JobFinancialSummary job={job} />
          <FinancialsForm job={job} />

          {job.status === 'completed' && canSeeTechPay && (
            <JobOverrideCommissionCard job={job} />
          )}

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Package className="h-4 w-4 text-warning" />
                </div>
                <CardTitle className="text-lg">Parts Used</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <AddPartFromCatalog jobId={job.id} />
              <JobPartsTable parts={parts} jobId={job.id} />
              <div className="flex justify-end gap-6 mt-4 pt-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Parts cost (internal): <span className="font-semibold text-foreground">{formatMoney(job.total_parts_cost)}</span>
                </div>
                <div className="font-semibold text-lg">
                  Charged to customer: {formatMoney(job.total_parts_sell ?? 0)}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-success/10 flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-success" />
                  </div>
                  <CardTitle className="text-lg">Payment History</CardTitle>
                </div>
                <Button onClick={() => setShowAddPayment(true)} size="sm">
                  Record Payment
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <JobPaymentsTable payments={payments} jobId={job.id} />
              <div className="space-y-2 mt-4 pt-4 border-t">
                <div className="flex justify-between">
                  <span className="font-semibold">Total Paid:</span>
                  <span className="font-bold text-lg text-success">{formatMoney(job.amount_paid)}</span>
                </div>
                {job.amount_outstanding !== null && (
                  <div className="flex justify-between">
                    <span className="font-semibold">Outstanding:</span>
                    <span className={`font-bold text-lg ${job.amount_outstanding > 0 ? 'text-destructive' : 'text-success'}`}>
                      {formatMoney(job.amount_outstanding)}
                    </span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Activity Log — always visible; quotes on the lead appear on the same timeline */}
      <JobActivityLog jobId={job.id} leadId={job.lead_id} />

      {/* Dialogs */}
      <AddJobPartDialog
        jobId={job.id}
        open={showAddPart}
        onOpenChange={setShowAddPart}
      />

      <AddPaymentDialog
        jobId={job.id}
        open={showAddPayment}
        onOpenChange={setShowAddPayment}
      />

      <CompleteJobDialog
        job={job}
        open={showCompleteJobDialog}
        onOpenChange={setShowCompleteJobDialog}
        onComplete={handleJobComplete}
      />

      <CancelJobDialog
        job={job}
        open={showCancelJobDialog}
        onOpenChange={setShowCancelJobDialog}
        onComplete={handleJobCancel}
      />

      <Dialog open={showRescheduleDialog} onOpenChange={setShowRescheduleDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Reschedule Job</DialogTitle>
            <DialogDescription>
              Select a new date and optional time window for this job.
              {rescheduleReasks && (
                <>
                  {' '}
                  {(job as { techs?: { name: string } | null }).techs?.name ?? 'The technician'} will be asked to
                  confirm it again in the app.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">New Date</label>
              <Calendar
                mode="single"
                selected={rescheduleDate}
                onSelect={setRescheduleDate}
                initialFocus
                className={cn("rounded-md border pointer-events-auto")}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Start Time</label>
                <Input
                  type="time"
                  value={rescheduleTimeStart}
                  onChange={(e) => setRescheduleTimeStart(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">End Time</label>
                <Input
                  type="time"
                  value={rescheduleTimeEnd}
                  onChange={(e) => setRescheduleTimeEnd(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRescheduleDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              disabled={!rescheduleDate || updateScheduledDate.isPending}
            >
              {updateScheduledDate.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Estimate Dialog */}
      {job.lead_id && (
        <EstimateFromLeadDialog
          open={showEstimateDialog}
          onOpenChange={setShowEstimateDialog}
          lead={{
            id: job.lead_id,
            customer_name: job.customer_name,
            customer_phone: job.customer_phone,
            customer_email: job.customer_email,
            customer_address: job.customer_address,
            issue_description: job.issue_description ?? '',
            quoted_price: job.quoted_price,
            customer_id: job.customer_id,
          }}
          onEstimateSent={() => {
            updateStatus.mutate({ jobId: job.id, status: 'quote_sent' });
          }}
        />
      )}

      {/* Generate Invoice Dialog */}
      <InvoiceFromJobDialog
        open={showInvoiceDialog}
        onOpenChange={setShowInvoiceDialog}
        job={{
          id: job.id,
          job_number: job.job_number,
          customer_id: job.customer_id,
          customer_name: job.customer_name,
          customer_email: job.customer_email,
          customer_phone: job.customer_phone,
          customer_address: job.customer_address,
          issue_description: job.issue_description,
          invoice_total: job.invoice_total,
          total_parts_cost: job.total_parts_cost,
          toll_cost: job.toll_cost,
        }}
      />

      {/* Link to existing project dialog */}
      {enableExtendedProjects && (
        <LinkJobToProjectDialog
          open={addToProjectOpen}
          onOpenChange={setAddToProjectOpen}
          jobId={job.id}
          alreadyLinkedProjectIds={jobProjects?.map(p => p.id) ?? []}
        />
      )}

      {/* Create new project with this job pre-linked */}
      {enableExtendedProjects && firstPipeline && (
        <CreateProjectDialog
          open={createProjectForJobOpen}
          onOpenChange={setCreateProjectForJobOpen}
          pipeline={firstPipeline}
          defaultJobId={job.id}
        />
      )}
    </div>
  );
}
